import { CaretRight } from '@/shared/icons/phosphor'
import { type Component, For, type JSX, Show } from 'solid-js'
import { Dynamic } from 'solid-js/web'
import { EmptyValue } from '../../primitives/EmptyValue/EmptyValue'

export type TimelineTone =
  | 'neutral'
  | 'positive'
  | 'closed'
  | 'info'
  | 'warning'
  | 'error'
  | 'accent'

export type TimelineOrientation = 'vertical' | 'horizontal'

export interface TimelineLane {
  id: string
  label: JSX.Element
}

export interface TimelineEntry {
  id: string
  icon?: Component<{ class?: string }>
  label: JSX.Element
  note?: JSX.Element
  off?: boolean
  added?: boolean
}

export interface TimelineItem {
  id: string
  icon?: Component<{ class?: string }>
  tone?: TimelineTone
  color?: string
  connector?: 'solid' | 'dashed' | 'none'
  title: JSX.Element
  meta?: JSX.Element
  body?: JSX.Element
  aside?: JSX.Element
  lanes?: Readonly<Record<string, readonly TimelineEntry[]>>
  onSelect?: () => void
  selected?: boolean
}

export interface TimelineProps {
  items: TimelineItem[]
  orientation?: TimelineOrientation
  lanes?: readonly TimelineLane[]
  class?: string
}

const toneClass: Record<TimelineTone, string> = {
  neutral: 'bg-[var(--color-primary-50)] text-[var(--color-primary-600)]',
  positive: 'bg-[var(--color-success-50)] text-[var(--color-success-700)]',
  closed: 'bg-[var(--color-neutral-100)] text-[var(--color-neutral-600)]',
  info: 'bg-[var(--color-info-50)] text-[var(--color-info-700)]',
  warning: 'bg-[var(--color-warning-50)] text-[var(--color-warning-700)]',
  error: 'bg-[var(--color-error-50)] text-[var(--color-error-600)]',
  accent: 'bg-[var(--color-violet-50)] text-[var(--color-violet-600)]',
}

const listClass: Record<TimelineOrientation, string> = {
  vertical: 'relative',
  horizontal:
    'relative grid grid-cols-1 gap-5 min-[720px]:auto-cols-fr min-[720px]:grid-flow-col min-[720px]:gap-6',
}

const itemClass: Record<TimelineOrientation, string> = {
  vertical: 'relative flex gap-3 pb-5 last:pb-0',
  horizontal: 'relative flex gap-3 min-[720px]:flex-col',
}

const connectorClass: Record<TimelineOrientation, string> = {
  vertical: 'absolute left-4 top-8 bottom-0 w-px -translate-x-1/2 bg-[var(--color-border)]',
  horizontal:
    'absolute left-4 top-8 -bottom-5 w-px -translate-x-1/2 bg-[var(--color-border)] min-[720px]:top-4 min-[720px]:bottom-auto min-[720px]:left-11 min-[720px]:-right-3 min-[720px]:h-px min-[720px]:w-auto min-[720px]:translate-x-0',
}

const dashedConnectorClass =
  'absolute left-4 top-7 bottom-0 w-0 -translate-x-1/2 border-l border-dashed border-[var(--color-control-border)]'

const colouredConnectorClass =
  'absolute left-4 top-7 bottom-0 w-px -translate-x-1/2 bg-[var(--color-border)]'

const LANE_COLUMNS =
  'grid-cols-[minmax(0,11rem)_repeat(var(--timeline-lanes),minmax(0,1fr))] @max-[640px]:grid-cols-1'

const selectableClass =
  'group -mx-2 -my-1.5 flex min-w-0 flex-1 gap-3 rounded-[var(--radius-8)] px-2 py-1.5 text-left transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary-500)]'

const Marker: Component<{ item: TimelineItem }> = (props) => (
  <Show
    when={props.item.color}
    fallback={
      <span
        class={`relative z-10 flex h-8 w-8 flex-none items-center justify-center rounded-full ${
          toneClass[props.item.tone ?? 'neutral']
        }`}
      >
        <Show when={props.item.icon} fallback={<span class="h-2 w-2 rounded-full bg-current" />}>
          {(Icon) => <Dynamic component={Icon()} class="h-4 w-4" />}
        </Show>
      </span>
    }
  >
    {(color) => (
      <span class="relative z-10 flex h-7 w-8 flex-none items-center justify-center">
        <span
          data-timeline-marker
          class="h-2.5 w-2.5 rounded-full"
          style={{ background: color() }}
        />
      </span>
    )}
  </Show>
)

const Entry: Component<{ entry: TimelineEntry }> = (props) => (
  <span
    data-timeline-entry={props.entry.id}
    data-off={props.entry.off || undefined}
    data-added={props.entry.added || undefined}
    class="flex items-start gap-1.5 text-[length:var(--text-sm)]"
    classList={{
      'text-[var(--color-text-muted)]': !props.entry.added,
      'text-[var(--color-primary-600)]': props.entry.added === true,
      'line-through': props.entry.off === true,
    }}
  >
    <Show when={props.entry.icon}>
      {(Icon) => <Dynamic component={Icon()} class="mt-0.5 h-4 w-4 flex-none" />}
    </Show>
    <span class="min-w-0">
      {props.entry.label}
      <Show when={props.entry.note}>
        <span class="ml-1.5 inline-block text-[length:var(--text-xs)] font-medium text-[var(--color-text-muted)]">
          {props.entry.note}
        </span>
      </Show>
    </span>
  </span>
)

const ItemText: Component<{ item: TimelineItem }> = (props) => (
  <>
    <span class="flex items-center gap-1 text-[length:var(--text-sm)] font-medium text-[var(--color-text)]">
      {props.item.title}
      <Show when={props.item.onSelect}>
        <CaretRight
          data-timeline-select-cue
          aria-hidden="true"
          class="h-3.5 w-3.5 flex-none text-[var(--color-text-subtle)] transition-transform group-hover:translate-x-0.5 group-hover:text-[var(--color-text)]"
        />
      </Show>
    </span>
    <Show when={props.item.meta}>
      <span class="block text-[length:var(--text-xs)] text-[var(--color-text-subtle)]">
        {props.item.meta}
      </span>
    </Show>
    <Show when={props.item.body}>
      <span class="mt-1 block text-[length:var(--text-sm)] text-[var(--color-text-muted)]">
        {props.item.body}
      </span>
    </Show>
  </>
)

const LaneContent: Component<{ item: TimelineItem; lanes: readonly TimelineLane[] }> = (props) => (
  <span data-timeline-lane-grid class={`grid min-w-0 flex-1 gap-x-5 gap-y-3 pt-1 ${LANE_COLUMNS}`}>
    <span class="block min-w-0">
      <ItemText item={props.item} />
    </span>
    <For each={props.lanes}>
      {(lane) => {
        const entries = () => props.item.lanes?.[lane.id] ?? []
        return (
          <span
            data-timeline-lane={lane.id}
            class="flex min-w-0 flex-col gap-1.5"
            classList={{ '@max-[640px]:hidden': entries().length === 0 }}
          >
            <span
              data-timeline-lane-label
              class="sr-only text-[length:var(--text-xs)] font-medium text-[var(--color-text-subtle)] @max-[640px]:not-sr-only @max-[640px]:block"
            >
              {lane.label}
            </span>
            <Show
              when={entries().length > 0}
              fallback={
                <span data-timeline-lane-empty class="text-[length:var(--text-sm)]">
                  <EmptyValue />
                </span>
              }
            >
              <For each={entries()}>{(entry) => <Entry entry={entry} />}</For>
            </Show>
          </span>
        )
      }}
    </For>
  </span>
)

const PlainHeading: Component<{ item: TimelineItem }> = (props) => [
  <div class="text-[length:var(--text-sm)] font-medium text-[var(--color-text)]">
    {props.item.title}
  </div>,
  <Show when={props.item.meta}>
    <div class="text-[length:var(--text-xs)] text-[var(--color-text-subtle)]">
      {props.item.meta}
    </div>
  </Show>,
]

const PlainContent: Component<{ item: TimelineItem }> = (props) => (
  <div class="min-w-0 flex-1 pt-1">
    <Show when={props.item.aside} fallback={<PlainHeading item={props.item} />}>
      {(aside) => (
        <div class="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
          <div class="min-w-0">
            <PlainHeading item={props.item} />
          </div>
          <div data-timeline-aside class="flex shrink-0 items-center gap-2">
            {aside()}
          </div>
        </div>
      )}
    </Show>
    <Show when={props.item.body}>
      <div class="mt-1 text-[length:var(--text-sm)] text-[var(--color-text-muted)]">
        {props.item.body}
      </div>
    </Show>
  </div>
)

export const Timeline: Component<TimelineProps> = (props) => {
  const orientation = () => (props.lanes?.length ? 'vertical' : (props.orientation ?? 'vertical'))
  const lanes = () => props.lanes ?? []

  const connectorFor = (item: TimelineItem, next: TimelineItem) => {
    if (next.connector === 'dashed') {
      return dashedConnectorClass
    }
    return item.color ? colouredConnectorClass : connectorClass[orientation()]
  }

  const content = (item: TimelineItem) => (
    <>
      <Marker item={item} />
      <Show when={lanes().length > 0} fallback={<PlainContent item={item} />}>
        <LaneContent item={item} lanes={lanes()} />
      </Show>
    </>
  )

  const list = () => (
    <ol
      data-timeline-orientation={orientation()}
      class={`${listClass[orientation()]} ${props.lanes?.length ? '' : (props.class ?? '')}`}
    >
      <For each={props.items}>
        {(item, index) => {
          const next = () => props.items[index() + 1]
          return (
            <li data-timeline-item={item.id} class={itemClass[orientation()]}>
              <Show when={next()?.connector !== 'none' && next()}>
                {(following) => (
                  <span
                    data-connector={following().connector}
                    class={connectorFor(item, following())}
                  />
                )}
              </Show>
              <Show when={item.onSelect} fallback={content(item)}>
                {(onSelect) => (
                  <button
                    type="button"
                    class={selectableClass}
                    classList={{
                      'bg-[var(--color-primary-50)]': item.selected === true,
                      'hover:bg-[var(--color-neutral-100)]': item.selected !== true,
                    }}
                    aria-expanded={item.selected === true}
                    data-selected={item.selected || undefined}
                    onClick={() => onSelect()()}
                  >
                    {content(item)}
                  </button>
                )}
              </Show>
            </li>
          )
        }}
      </For>
    </ol>
  )

  return (
    <Show when={props.lanes?.length} fallback={list()}>
      <div
        class={`@container ${props.class ?? ''}`}
        style={{ '--timeline-lanes': String(lanes().length) }}
      >
        <div
          data-timeline-lane-header
          aria-hidden="true"
          class={`grid gap-x-5 pb-2 pl-11 text-[length:var(--text-xs)] font-medium text-[var(--color-text-muted)] @max-[640px]:hidden ${LANE_COLUMNS}`}
        >
          <span />
          <For each={lanes()}>{(lane) => <span class="min-w-0">{lane.label}</span>}</For>
        </div>
        {list()}
      </div>
    </Show>
  )
}
